import React from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { makeStyles } from "@material-ui/core/styles";
import BuildIcon from "@material-ui/icons/Build";
import FloatingToolbarItem from "./FloatingToolbarItem";
import useInputsV3ToolbarSummary, {
  refreshInputsV3ToolbarSummary,
} from "./useInputsV3ToolbarSummary";
import ModalHeader from "../ModalHeader";
import Button from "../CustomButtons/Button";
import { updateInputsV3Maintenance } from "../../services/inputsV3Service";
import {
  grayColor,
  warningColor,
} from "../../assets/jss/material-dashboard-pro-react";

const TTL_OPTIONS = [
  { value: "3h", label: "3 horas" },
  { value: "24h", label: "24 horas" },
  { value: "72h", label: "72 horas" },
];

const useStyles = makeStyles(() => ({
  modal: {
    borderRadius: 6,
    overflow: "visible",
  },
  modalBody: {
    padding: "16px 18px 10px",
    fontSize: 14,
    color: grayColor[1],
  },
  modalFooter: {
    padding: "8px 16px 14px",
  },
  panel: {
    border: `1px solid ${grayColor[7]}`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 10,
    background: "#fafafa",
  },
  panelWarning: {
    border: `1px solid ${warningColor[0]}`,
    background: "#fff8e8",
  },
  panelTitle: {
    fontWeight: 600,
    marginBottom: 4,
    color: grayColor[0],
  },
  panelText: {
    color: grayColor[1],
    fontSize: 13,
  },
  ttlGroup: {
    display: "flex",
    gap: 8,
    marginTop: 6,
  },
  ttlChip: {
    border: `1px solid ${grayColor[7]}`,
    borderRadius: 16,
    padding: "4px 12px",
    fontSize: 12,
    cursor: "pointer",
    userSelect: "none",
    background: "#fff",
    color: grayColor[1],
    transition: "all 0.15s ease",
  },
  ttlChipSelected: {
    background: warningColor[0],
    borderColor: warningColor[0],
    color: "#fff",
    fontWeight: 600,
  },
  ttlChipDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  claimRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: grayColor[1],
    marginTop: 2,
  },
}));

function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_err) {
    return "-";
  }
}

function ttlHoursOf(choice) {
  if (choice === "24h") return 24;
  if (choice === "72h") return 72;
  return 3;
}

function projectedExpiry(ttlChoice) {
  const ttlHours = ttlHoursOf(ttlChoice);
  return new Date(Date.now() + ttlHours * 60 * 60 * 1000).toISOString();
}

export default function MaintenanceToolbarItem({
  _mobile,
  _position,
  ...rest
}) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [myClaims, setMyClaims] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [pendingTtl, setPendingTtl] = React.useState("3h");
  const mountedRef = React.useRef(true);
  const toolbarSummary = useInputsV3ToolbarSummary();

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!mountedRef.current) return;
    setStatus(toolbarSummary.maintenance || null);
    setMyClaims(
      Array.isArray(toolbarSummary.claims) ? toolbarSummary.claims : [],
    );
  }, [toolbarSummary.maintenance, toolbarSummary.claims]);

  const handleOpen = () => {
    setOpen(true);
    refreshInputsV3ToolbarSummary().catch(() => {});
  };

  const persistAction = async ({ action, ttl_choice }) => {
    setSaving(true);
    try {
      const payload = await updateInputsV3Maintenance({ action, ttl_choice });
      const nextStatus = payload?.maintenance || null;
      setStatus(nextStatus);
      refreshInputsV3ToolbarSummary().catch(() => {});
      window.dispatchEvent(
        new CustomEvent("inputsV3MaintenanceChanged", {
          detail: nextStatus,
        }),
      );
    } catch (err) {
      console.error("Error updating V3 maintenance status:", err);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleActivate = () =>
    persistAction({ action: "activate", ttl_choice: pendingTtl });
  const handleExtend = () =>
    persistAction({ action: "extend", ttl_choice: pendingTtl });
  const handleDeactivate = () => persistAction({ action: "deactivate" });

  const active = !!status?.is_active;
  const activeJob = status?.active_job;
  const claimedBy = status?.activated_by;
  const expiresAt = active ? status?.expires_at : projectedExpiry(pendingTtl);
  const currentTtlChoice = active ? status?.ttl_choice : pendingTtl;

  // Badge: vermelho se houver QUALQUER claim ativa do user (mesmo que
  // a impl atual nao esteja em manutencao). Usamos warning visual no
  // dot para diferenciar de "ativo nesta impl".
  const hasAnyClaim = (myClaims || []).length > 0;
  const badgeOn = active || hasAnyClaim;

  const tooltip = active
    ? "Implementação em manutenção"
    : hasAnyClaim
      ? `Você mantém ${myClaims.length} implementação(ões) em manutenção`
      : "Manutenção da implementação";

  return (
    <>
      <FloatingToolbarItem
        icon={<BuildIcon fontSize="inherit" />}
        badgeDot={badgeOn}
        tooltip={tooltip}
        mobileLabel={tooltip}
        onClick={handleOpen}
        _mobile={_mobile}
        _position={_position}
        {...rest}
      />
      <Dialog
        classes={{ paper: classes.modal }}
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <ModalHeader
          id="inputs-v3-maintenance-title"
          title="Manutenção da implementação"
          subtitle="Controle temporário para desenvolvimento local de processamentos V3."
          pills={[
            {
              key: active ? "active" : "inactive",
              label: active ? "Ativo" : "Inativo",
              tone: active ? "warning" : "neutral",
              dot: active,
            },
          ]}
          onClose={() => setOpen(false)}
          closeDisabled={saving}
          closeLabel="Fechar"
        />
        <DialogContent className={classes.modalBody}>
          <div style={{ marginBottom: 12 }}>
            Com manutenção ativa, a produção não inicia processamentos V3 desta
            implementação. Apenas seu Functions local (com{" "}
            <code>DEV_WORKER_USERNAME</code> configurado) atua nela.
          </div>

          <div className={classes.panel}>
            <div className={classes.panelTitle}>
              {active ? "Janela ativa" : "Tempo de validade"}
            </div>
            <div className={classes.ttlGroup}>
              {TTL_OPTIONS.map((opt) => {
                const selected = currentTtlChoice === opt.value;
                const disabled = saving;
                return (
                  <span
                    key={opt.value}
                    className={[
                      classes.ttlChip,
                      selected ? classes.ttlChipSelected : "",
                      disabled ? classes.ttlChipDisabled : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (disabled) return;
                      setPendingTtl(opt.value);
                    }}
                  >
                    {opt.label}
                  </span>
                );
              })}
            </div>
            <div className={classes.panelText} style={{ marginTop: 8 }}>
              {active
                ? `Expira em ${formatDateTime(expiresAt)}. A janela renova automaticamente a cada atividade do backend.`
                : `Se ativado agora com ${ttlHoursOf(pendingTtl)}h, expira em ${formatDateTime(expiresAt)}.`}
            </div>
          </div>

          {active && claimedBy ? (
            <div className={classes.panel}>
              <div className={classes.panelTitle}>Reivindicada por</div>
              <div className={classes.claimRow}>
                <span>{claimedBy.name || claimedBy.email}</span>
                <span style={{ color: grayColor[2] }}>
                  desde {formatDateTime(status?.activated_at)}
                </span>
              </div>
            </div>
          ) : null}

          {hasAnyClaim ? (
            <div className={classes.panel}>
              <div className={classes.panelTitle}>
                Suas manutenções ativas ({myClaims.length})
              </div>
              {myClaims.map((c) => (
                <div key={c.implementation_id} className={classes.claimRow}>
                  <span>{c.implementation_app}</span>
                  <span style={{ color: grayColor[2] }}>
                    expira {formatDateTime(c.expires_at)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <div
            className={`${classes.panel} ${
              activeJob ? classes.panelWarning : ""
            }`}
          >
            <div className={classes.panelTitle}>Processamento atual</div>
            {activeJob ? (
              <div className={classes.panelText}>
                Há um job em andamento: {activeJob.job_type}{" "}
                {activeJob.subject ? `· ${activeJob.subject}` : ""} ·{" "}
                {activeJob.year}-{String(activeJob.month).padStart(2, "0")}
              </div>
            ) : (
              <div className={classes.panelText}>
                Não há job em andamento neste momento.
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <Button
            onClick={() => setOpen(false)}
            color="black"
            simple
            disabled={saving}
          >
            Fechar
          </Button>
          {active ? (
            <>
              <Button
                onClick={handleExtend}
                color="warning"
                simple
                disabled={saving}
              >
                Estender ({ttlHoursOf(pendingTtl)}h)
              </Button>
              <Button
                onClick={handleDeactivate}
                color="danger"
                simple
                disabled={saving}
              >
                Soltar manutenção
              </Button>
            </>
          ) : (
            <Button
              onClick={handleActivate}
              color="warning"
              simple
              disabled={saving}
            >
              Ativar manutenção ({ttlHoursOf(pendingTtl)}h)
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

MaintenanceToolbarItem.propTypes = {
  _mobile: PropTypes.bool,
  _position: PropTypes.string,
};
